import { CustomerReviews } from "~/common/Reviews/reviewModels";
import { ProductAd, ProductCategory } from "~/common/Store/models/ProductPage";
import {
  QualityRatingLabel,
  QualityRatingMembership,
} from "~/common/Store/models/QualityRating";
import { Video } from "~/common/Store/models/Video";
import PageResult from "~/common/types/PageResult";
import { CarouselItem } from "./Carousel/CarouselItem";
import { VirtualGroupingOptionsToMap } from "./VirtualGrouping/VirtualGroupingModel";

export interface ProductDetailPageResult {
  accessories: Accessory[];
  actualVendorItemNumber: string;
  breadCrumbs: BreadCrumbItem[];
  divideByUnit: string;
  divideQuantity: number;
  originalId: string;
  oneToFiveStarRating: number;
  certifications: ProductCertification[];
  customerExclusivity: CustomerExclusivity;
  customerReviews: CustomerReviews;
  productImages: Image[];
  modelFiles?: ModelFiles;
  productImageDisclaimer: string;
  primaryBrand: ProductBrand;
  itemNumber: string;
  itemNumberId: number;
  defaultCategory: ProductCategory;
  title: string;
  upc: string;
  highlights: string[];
  stockMessage: string;
  isQuickShip: boolean;
  isCommonCarrier: boolean;
  restrictItemExpeditedShipping: boolean;
  restrictedItemShippingText: string;
  videos: Video[];
  customersAlsoViewedCarouselItems: CarouselItem[];
  otherProductsFromThisLine: CarouselItem[];
  youMayAlsoNeedCarouselItems: CarouselItem[];
  showYouMayAlsoNeedAllItemsLink: boolean;
  productAds: ProductAd[];
  reviews: CustomerReviews;
  otherProductsCarouselPromotion: OtherProductsCarouselPromotion;
  price: Price;
  unitOfMeasure: string;
  unitsPerPackaging: number;
  sampleProgram?: SampleProgram;
  specificationTable: SpecificationTable;
  productCutDown?: ProductCutDown;
  link: string;
  compatibleModelItems: CompatibleModelItem[];
  compatibleModelItemsCount: number;
  compatibleWithLink: string;
  variationMembership: VariationMembership;
  equivalentItems: EquivalentItem[];
  isAutomaticReorderEligible: boolean;
  isReplacementEligible: boolean;
  isRequestAQuoteEnabled: boolean;
  isWebstaurantPlusEligible: boolean;
  isPart: boolean;
  isDemotedPart: boolean;
  isNewProduct: boolean;
  minimumBuy: number;
  mustBuy: number;
  startBuy: number;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  showGoodBetterBestBanner: boolean;
  showQuantityInSpecificationTable: boolean;
  grouping: boolean;
  groupingAttributes: GroupingAttributes;
  isInStock: boolean;
  imagePaths360Degree?: string[];
  manufacturerItemNumberDisplayText?: string;
  selectedItemNumber?: string;
  feedIdentifier: string;
  productDocuments: ProductDocument[];
  productDisclaimer: ProductDisclaimer;
  productReplacement: ProductReplacement;
  linkedResources: LinkedResource[];
  hasDropShip: boolean;
  hasFreeShipping: boolean;
  hasWebPlusDiscountedShipping: boolean;
  allowWebPlusDiscountedShipping: boolean;
  breakdownOptions?: BreakdownOptions[];
  hasPartsOrAccessories: boolean;
  hasRequiredAccessories: boolean;
  extendedDescription: string;
  isCustomizable: boolean;
  customizationRequired: boolean;
  customizationUrl: string;
  maximumCustomizationPrice: number;
  minimumCustomizationPrice: number;
  outletOffer: {
    price: number;
    link: string;
  };
  rawClientUrl: string;
  youMayAlsoNeedUrl: string;
  priceIncreaseAdvertisement: PriceIncreaseAdvertisement;
  qualityRatingMembership: QualityRatingMembership;
  questionsAndAnswers: QuestionAndAnswer[];
  header: string;
  noticesAndWarnings: NoticesAndWarnings;
  template: Templates;
  mixAndMatchUrl: string;
  purchasingGroupUrl: string;
  showRewardsCardAd: boolean;
  expandedDescription: ExpandedDescriptionProductDetailInfo;
  outletAttributes: OutletAttributes;
  displayInstallationService: boolean;
  isThirdPartyExtendedWarrantyEligible: boolean;
  extendedWarranties: ExtendedWarranty[];
  serviceLevelId: number;
  serviceTierId: number;
  warningContent?: string;
  isVirtualPart: boolean;
  seeMoreProductsCarouselItems: CarouselItem[];
  showEverlivingProductPage: boolean;
  showSeeMoreProductsCarousel: boolean;
  showVendorItemNumber: boolean;
  sideBySideProductCompareItem: SideBySideProductCompareItem;
  sourceVendor: SourceVendor;
  isConsumable: boolean;
  relatedSearchTerms: RelatedSearchTermResponse[];
  isRecAiEligible: boolean;
  canonicalUrl: string;
  allowOutOfStockPurchase: boolean;
  weightInPounds: number;
  requestAQuoteUrl: string;
  seoUpc: string;
  description: string;
  mediaViewerV2IsEnabled?: boolean;
  isCpcMinimalPageVersion?: boolean;
}

export interface BreadCrumbItem {
  displayText: string;
  link: string;
}

export interface Image {
  thumbnail: string;
  large: string;
  extraLarge: string;
  extraExtraLarge: string;
  isPrimaryDetailImage: boolean;
  isPrimaryListingImage: boolean;
  imageNote?: string;
  imageNotesCoordinates?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  alternateText?: string;
}

export type ModelFiles = {
  relativeGlbFilePath: string;
  absoluteGlbFilePath: string;
  relativeUsdzFilePath: string;
  absoluteUsdzFilePath: string;
  hasBackground: boolean;
};

export interface SourceVendor {
  name: string;
  code: string;
  hidden: boolean;
}

export interface NoticesAndWarnings {
  isSpecialOrder: boolean;
  hasORMD: boolean;
  isHazardousProduct: boolean;
  hasCompatibilityWarning: boolean;
  hasSubstituteItemWarning: boolean;
  hasSerialNumberWarning: boolean;
}

export interface OtherProductsCarouselPromotion {
  text: string;
  image: string;
  url: string;
  showShopAllTile: boolean;
}

export interface Price {
  applyTypeLPriceToPlatinumPlus: boolean;
  displayMinimumAdvertisedPrice: boolean;
  displaySalePrice: boolean;
  hasLotQuantityDiscounts: boolean;
  hasQuantityDiscounts: boolean;
  hasSingleLotQuantityDiscount: boolean;
  hideWhyDoWeDoThis?: boolean;
  minimumAdvertisedPriceOverride: {
    price?: number;
    priceLevel5: number;
  };
  minimumAdvertisedPriceProperties: {
    price: number;
    type: string;
  };
  originalPrice?: number;
  price: number;
  quantityDiscounts: QuantityDiscount[];
  saleEndDate?: string;
  salePrice: number;
  saleStartDate?: string;
}

export interface QuantityDiscount {
  endQuantity?: number;
  isLotDiscount: boolean;
  price: number;
  quantity: number;
  startQuantity: number;
}

export interface SampleProgram {
  isActive: boolean;
  labelType: string;
  maxQuantityPerCustomer: number;
  maxQuantityPerOrder: number;
  sampleItemNumber: string;
  sampleItemNumberId: number;
  sampleProduct: ProductDetailPageResult;
}

export interface SpecificationTable {
  specifications: Specification[];
  name: string;
}

export interface Specification {
  id: number;
  types: SpecificationType[];
  classification: string;
}

export interface SpecificationType {
  name: string;
  value: string;
}

export interface ProductBrand {
  name: string;
  link: string;
  imageLink: string;
  showBrandTile: boolean;
  brandedPrimaryCategoryName: string;
  categoryLinkWithVendor: string;
}

export interface ProductCutDown {
  cutDownPrice: number;
  minimumHeight: number;
  maximumHeight: number;
  numberOfLegs: number;
  hasCasters: boolean;
  casterHeight: number;
  casterItemNumber: string;
  casterItemPrice: number;
  videoId: string;
  cutDownVideo: Video;
}

export interface CompatibleModelItem {
  itemNumberId: number;
  urlLabel: string;
  link: string;
}

export interface VariationMembership {
  variationGroups: VariationGroup[];
}

export interface VariationGroup {
  displayTypeId: number;
  isSorted: boolean;
  hideThumbnails: boolean;
  optionName: string;
  variationGroupItems: VariationGroupItem[];
  id?: number;
}

export interface VariationGroupItem {
  itemNumber: string;
  displayText: string;
  variationText: string;
  thumbnail: string;
  seoLink: string;
  groupingMasterLink?: string;
  isWebstaurantPlusEligible: boolean;
  itemOrderBy: number;
  isInStock: boolean;
  productPrice: Price;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
}

export interface EquivalentItem {
  oemId: string;
  oemNum: string;
  oemManufacturer: string;
  isVirtual: boolean;
  oemFull: string;
  link: string;
}

export type CreditKeyConfig = {
  publicKeyId?: string;
  minimumDisplayPrice?: number;
};

export interface ProductDetailTemplateGroup extends PageResult {
  productTemplates: ProductDetailPageResult[];
  productDetailGroupingSelect: ProductDetailGroupingSelect[];
  internalNotesRecentUpdateResult?: InternalNotesRecentUpdateResult;
  isGoogleView?: boolean;
  creditKeyConfig?: CreditKeyConfig;
  virtualGroupOptionsMap?: VirtualGroupingOptionsToMap[];
  groupMembers?: GroupMember[];
  isUtmMediumCPC?: boolean;
}

export interface ProductDetailGroupingSelect {
  itemNumber: string;
  isInStock?: boolean;
  previewImage?: string;
  configurationOptions: ConfigurationOption[];
  customerReviewSummary: string;
}

export interface ConfigurationOption {
  name: string;
  value: string;
  sortIndex?: number;
}

export interface GroupingAttributes {
  isVirtual: boolean;
  isGroupMaster: boolean;
  groupMembers: GroupMember[];
  virtualGroupItemNumber: string;
}

export interface GroupMember {
  optionLabel: string;
  displayName: string;
  itemNumber: string;
  itemNumberId: number;
  unitOfMeasurement: string;
  configurationOptions: GroupingConfigurationOption[];
  price: Price;
  title: string;
  isInStock: boolean;
  minimumBuy: number;
  mustBuy: number;
  startBuy: number;
  mustPurchaseMoreThanOne: boolean;
  primaryImagePath: string;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
}

export interface GroupingConfigurationOption {
  name: string;
  value: string;
}

export interface ProductDocument {
  name: string;
  filePath: string;
  thumbnailPath: string;
  type: string;
  customType?: string;
  externalUrl?: string;
  externalUrlTitle?: string;
}

export interface ProductDisclaimer {
  showWarrantyInformation: boolean;
  warrantyInformation: string;
  prop65Warning: string;
  disclaimerText?: string;
  disclaimerRibbon?: {
    text: string;
    ribbonType: string;
  };
  displayAb1200Disclaimer?: boolean;
  ab1200Chemicals?: string[];
  spanishAb1200Chemicals?: string[];
}

export type ProductReplacement = {
  replacementItemNumber: string;
  replacementLink: string;
};

export interface LinkedResource {
  title: string;
  link: string;
}

export type InternalNotesRecentUpdateResult = {
  dateModified: string;
  userName: string;
};

export interface BreakdownOptions {
  price: Price;
  unitOfMeasurement: string;
  quantityPerPackaging: number;
  link: string;
  isWebstaurantPlusEligible: boolean;
  itemNumber: string;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
}

export interface QuestionAndAnswer {
  question: string;
  answer: string;
}

export interface PriceIncreaseAdvertisement {
  startPercentIncrease: number;
  endPercentIncrease: number;
  startDate: string;
  endDate: string;
  priceIncreaseDate: string;
}

export interface ProductCertification {
  id: number;
  name: string;
  description: string;
  type: number;
  resourcePath: string;
}

export interface ExpandedDescriptionProductDetailInfo {
  compareProducts?: CompareProducts;
  featuredReview: FeaturedReview;
  sections: ExpandedDescriptionTextSection[];
  sectionsGrouping: ExpandedDescriptionTextSectionGrouping[];
  specificationSummary: ExpandedDescriptionSpecificationTable[];
}

export interface CompareProducts {
  enabled: boolean;
  products: ProductDetailPageResult[];
  comparableClassificationIds: number[];
}

export interface FeaturedReview {
  oneToFiveStarRating: number;
  reviewText: string;
}

export interface ExpandedDescriptionTextSection {
  headline: string;
  body: string;
  image: string;
  imageHasPadding: boolean;
  type: ExpandedDescriptionTextSectionTypes;
  customCalculatorFileName: string;
  sortOrder: number;
  video: Video | undefined;
  isVideoRepeating: boolean;
}

export interface ExpandedDescriptionTextSectionGrouping {
  sortOrder: number;
  type: ExpandedDescriptionTextSectionTypes;
  sections: ExpandedDescriptionTextSection[];
}

export enum ExpandedDescriptionTextSectionTypes {
  Introduction,
  TextImageDouble,
  TextImageLeft,
  TextImageRight,
  TextImageTriple,
  CustomCalculator,
  Unknown,
}

export type ExpandedDescriptionSpecificationTable = {
  name: string;
  specifications: ExpandedDescriptionSpecification[];
};

export type ExpandedDescriptionSpecification = {
  name: string;
  value: string;
};

export type RelatedSearchTermResponse = {
  id: number;
  relatedProductSearchTerm: string;
  relatedProductSearchTermEncoded: string;
  description: string;
};

export type OutletAttributes = {
  highlights: string;
  newProductLink: string;
  originalPrice: number;
  originalProductAvailable: boolean;
  outletProductId: number;
  callForPricing?: boolean;
};

export enum Templates {
  StandardTemplate,
  ExpandedDescriptionTemplate,
  OutletTemplate,
  SideBySideTemplate,
  VirtualPartsTemplate,
}

export interface ExtendedWarranty {
  id: number;
  name: string;
  price: number;
  isThirdParty: boolean;
}

export enum ExtendedWarrantyViewType {
  RadioButtonView = "1",
  ModalView = "base",
}

export interface CustomerExclusivity {
  disallowNonMemberToPurchase: boolean;
  allowPlatinumToPurchase: boolean;
  allowWebstaurantPlusToPurchase: boolean;
}

export interface Accessory {
  name: string;
  required: boolean;
  extendedInfo: string;
  options: {
    id: number;
    price: number;
    description: string;
    available: boolean;
  }[];
}

export type UserAutoReorder = {
  name: string;
  isActive: boolean;
  creditCardId: number;
  desiredOrderDate: string;
  id: number;
};

export type UserProductPurchasingData = {
  previouslyPurchasedOrderNumber: number;
  previouslyPurchasedDateFormatted: string;
  hasMultipleAutoReorders: boolean;
  activeAutoReorderId: number;
  nextAutoReorderDateFormatted: string;
  activeAutoReorders: UserAutoReorder[];
};

export interface SideBySideProductCompareItem {
  bannerDescription: string;
  bannerMessage: string;
  compareMessage: string;
  compareProductItemNumber: string;
  description: string;
  displayType: string;
  hasFreeShipping: boolean;
  hasSideBySideProductCompare: boolean;
  imagePath: string;
  isWebstaurantPlusEligible: boolean;
  link: string;
  oneToFiveStarRating: number;
  overallRating: number;
  price: Price;
  ratingCount: number;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  startBuy: number;
  title: string;
  unitOfMeasure: string;
  isRecommendedSimilarItem: boolean;
}

export interface SpotlightBannerItem {
  bannerDescription?: string;
  bannerMessage?: string;
  compareMessage?: string;
  brand?: string;
  brandLogo?: string;
  itemNumber?: string;
  description: string;
  displayType?: string;
  hasFreeShipping: boolean;
  hasSideBySideProductCompare?: boolean;
  headlineText?: string;
  imagePath: string;
  isWebstaurantPlusEligible: boolean;
  label?: QualityRatingLabel;
  link: string;
  oneToFiveStarRating: number;
  overallRating: number;
  price: Price;
  ratingCount: number;
  ratingDescription?: string;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  startBuy: number;
  title?: string;
  unitOfMeasure: string;
  isRecommendedSimilarItem?: boolean;
  compareProductItemNumber?: string;
}
export interface AutoReorder {
  isToggleEnabled: boolean;
  isAddToExistingAutoReorder: boolean;
  newAutoReorderInterval: string;
  selectedExistingAutoReorder: UserAutoReorder;
  autoReorderDropdownValue?: string;
}
